import React from 'react'
import { View, Text, StyleSheet } from 'react-native'
import { v4 as uuidv4 } from 'uuid'
import { ListItemLinkButton } from '../ListItemLinkButton'
import { Divider } from 'react-native-elements'
import spacing from '../../styles/spacing'
import shared from '../../styles/shared'
import MSFESButton from '../MSFESButton'
import colors from '../../styles/colors'
import { FieldMapperDropdowns } from './FieldMapperDropdowns'
import { viewMode } from '../../screens/EntityAddScreen'
import BannerWithButton from '../BannerWithButton'

export const DoubleFieldListMapper = React.forwardRef((props, ref) => {
  const { mode } = props
  const fieldList1 = props.fieldList1
  const fieldList2 = props.fieldList2

  const mappedFields = props.mappedFields

  // for each mapped field, we will show the source and destination fields and a delete button.
  // under those, we will show a dropdown for the field1 with only unmapped fields. When field1 is selected, we will show the field2, same only unmapped.
  // when both are filled, they are added to mapped fields.

  const entityFieldMapSourceChosen = (value, index) => {
    const mappedFields = [...props.mappedFields]
    mappedFields[index].source = value
    props.onFieldmapChanged(mappedFields)
  }
  const entityFieldMapDestinationChosen = (value, index) => {
    const mappedFields = [...props.mappedFields]
    mappedFields[index].destination = value
    props.onFieldmapChanged(mappedFields)
  }

  const onAddMappedFieldPressed = () => {
    const mappedFields = [...(props.mappedFields ?? [])]
    mappedFields.push({ source: null, destination: null, offline_id: uuidv4 })
    props.onFieldmapChanged(mappedFields)
  }

  const deleteMapRow = (mappedFieldSet, index) => {
    const mappedFields = [...props.mappedFields]
    mappedFields.splice(index, 1)

    props.onFieldmapChanged(mappedFields)
  }

  const textSymbol = [
    {
      backgroundColor: colors.inputBottomBorder,
      paddingHorizontal: 4,
      paddingVertical: 2,
      borderRadius: 4
    }
  ]

  return (
    <View style={[shared.debugOff, { flexDirection: 'column' }]}>
      {(mappedFields ?? []).map((mappedFieldSet, index) => {
        const sourceValue = mappedFieldSet.source?.id
        const targetValue = mappedFieldSet.destination?.id

        const sourceTitle = mappedFieldSet.source?.title
        const targetTitle = mappedFieldSet.destination?.title

        let requiresParentMapped = false
        let hasDrillIntoChildFieldExceptionalCase = false
        let parentFieldTitle = null
        let mappedParent = null
        if (sourceTitle?.includes('.*.')) {
          const lastDot = sourceTitle.lastIndexOf('.')
          parentFieldTitle = sourceTitle.slice(0, lastDot)
          // this is a nested field.
          // if the parent is not mapped, we cannot continue for this one.
          mappedParent = mappedFields.find(
            (m) => m.source?.title == parentFieldTitle
          )

          if (!mappedParent) {
            // we need a special case for mapped parents.
            // if the XML file has a repeating text field, then we may need to insert that text into the child field of a child entity.
            // e.g. ResponseComments.comment repeats and is text.
            // so we need to map ResponseComments.comment to CAD_NOTES entity type, and map ResponseComments.comment to CAD_NOTES.text_entered FIELD.
            // if there is another field mapped against this one that doesn't match its index, then user is trying to do this.
            const otherMappedField = mappedFields.find((m, i) => {
              return m.source?.title == sourceTitle && i < index
            })
            if (otherMappedField) {
              mappedParent = otherMappedField

              hasDrillIntoChildFieldExceptionalCase = true
            } else {
              requiresParentMapped = true
            }
          }
        }

        if (requiresParentMapped) {
          return (
            <View key={index}>
              <BannerWithButton>
                This field cannot be mapped to a child entity type field unless
                a parent field is mapped to the entity type. Alternatively, you
                can connect it to the parent entity type here:
              </BannerWithButton>
              <FieldMapperDropdowns
                fieldList1={fieldList1}
                fieldList2={fieldList2}
                field1Value={sourceValue}
                field2Value={targetValue}
                entityTypeFieldList2={props.entityTypeFieldList2}
                onField1Selected={(value) => {
                  entityFieldMapSourceChosen(value, index)
                }}
                onField2Selected={(value) =>
                  entityFieldMapDestinationChosen(value, index)
                }
              />
            </View>
          )
        }

        return (
          <>
            {hasDrillIntoChildFieldExceptionalCase && (
              <BannerWithButton>
                *** Mapping this field again will map it to a child property
                also.***
              </BannerWithButton>
            )}
            <View
              key={index}
              style={[
                {
                  flexDirection: 'row',
                  flex: 1,
                  borderTopWidth: StyleSheet.hairlineWidth,
                  borderColor: colors.lightestGray,
                  paddingTop: spacing.m0_5
                },
                shared.debugOff
              ]}
            >
              {sourceValue && targetValue && (
                <View style={{ flex: 1, justifyContent: 'center' }}>
                  <Text>
                    <Text style={textSymbol}>{sourceTitle}</Text> maps to{' '}
                    <Text style={textSymbol}>
                      {targetTitle} ({targetValue})
                    </Text>
                  </Text>
                </View>
              )}
              {!(sourceValue && targetValue) && (
                <FieldMapperDropdowns
                  mappedParent={mappedParent}
                  fieldList1={fieldList1}
                  fieldList2={fieldList2}
                  field1Value={sourceValue}
                  field2Value={targetValue}
                  entityTypeFieldList2={props.entityTypeFieldList2}
                  onField1Selected={(value) =>
                    entityFieldMapSourceChosen(value, index)
                  }
                  onField2Selected={(value) =>
                    entityFieldMapDestinationChosen(value, index)
                  }
                />
              )}

              {mode !== viewMode && (
                <View>
                  <MSFESButton
                    icon={{ type: 'font-awesome', name: 'trash' }}
                    type="delete"
                    onPress={() => deleteMapRow(mappedFieldSet, index)}
                  ></MSFESButton>
                </View>
              )}
              <Divider />
            </View>
          </>
        )
      })}
      {mode !== viewMode && (
        <ListItemLinkButton
          title={`Add Mapped Field`}
          type="add"
          onPress={onAddMappedFieldPressed}
        />
      )}
    </View>
  )
})
